import { useState, useRef, useEffect, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from './ui/Toast'
import { Drawer } from './ui/Drawer'
import { Input } from './ui/Input'
import { Button } from './ui/Button'
import { SiteCreateModal } from './site/SiteCreateModal'

interface QuickCreateItem {
  id: string
  label: string
  icon: string
  fields: QuickField[]
  endpoint: string
  navigateTo?: (slug: string) => string
}

interface QuickField {
  name: string
  label: string
  type: 'text' | 'email' | 'tel' | 'select' | 'textarea'
  required?: boolean
  options?: { value: string; label: string }[]
  placeholder?: string
}

const QUICK_ITEMS: QuickCreateItem[] = [
  {
    id: 'chat',
    label: 'New Chat',
    icon: '\u{1F4AC}',
    endpoint: '',
    fields: [],
  },
  {
    id: 'client',
    label: 'New Client',
    icon: '\u25C9',
    endpoint: '/clients',
    navigateTo: (slug) => `/clients/${slug}`,
    fields: [
      { name: 'company_name', label: 'Company Name', type: 'text', required: true, placeholder: 'Acme Corp' },
      { name: 'contact_name', label: 'Contact Name', type: 'text', placeholder: 'John Smith' },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'john@acme.com' },
      { name: 'phone', label: 'Phone', type: 'tel', placeholder: '(555) 123-4567' },
    ],
  },
  {
    id: 'proposal',
    label: 'New Proposal',
    icon: '\u2630',
    endpoint: '/proposals',
    navigateTo: (slug) => `/proposals/${slug}`,
    fields: [
      { name: 'title', label: 'Name', type: 'text', required: true, placeholder: 'Security System Upgrade' },
    ],
  },
  {
    id: 'dispatch',
    label: 'New Work Order',
    icon: '\u2709',
    endpoint: '/work-orders',
    navigateTo: (slug) => `/dispatch/${slug}`,
    fields: [
      { name: 'title', label: 'Title', type: 'text', required: true, placeholder: 'Camera installation' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Brief description...' },
      { name: 'type', label: 'Type', type: 'select', required: true, options: [
        { value: 'service', label: 'Service' }, { value: 'install', label: 'Install' },
        { value: 'maintenance', label: 'Maintenance' }, { value: 'inspection', label: 'Inspection' },
      ]},
      { name: 'priority', label: 'Priority', type: 'select', required: true, options: [
        { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' },
        { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
      ]},
    ],
  },
  {
    id: 'site',
    label: 'New Site',
    icon: '\u229E',
    endpoint: '/sites',
    navigateTo: (slug) => `/sites/${slug}`,
    fields: [],
  },
  {
    id: 'ticket',
    label: 'New Ticket',
    icon: '\u{1F3AB}',
    endpoint: '/tickets',
    navigateTo: (slug) => `/tickets?id=${slug}`,
    fields: [
      { name: 'subject', label: 'Subject', type: 'text', required: true, placeholder: 'Camera offline at lobby' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the issue...' },
      { name: 'priority', label: 'Priority', type: 'select', required: true, options: [
        { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' },
        { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
      ]},
    ],
  },
]

function FABMenuItem({ label, icon, onClick, delay }: { label: string; icon: string; onClick: () => void; delay: number }) {
  const [hovered, setHovered] = useState(false)

  const circleStyle: CSSProperties = {
    width: 42, height: 42, borderRadius: '50%',
    background: 'rgba(18,18,24,.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${hovered ? 'var(--accent)' : 'var(--border-h)'}`,
    boxShadow: hovered ? 'var(--sh-glow)' : 'var(--sh-md)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all var(--dur-f)',
    fontSize: 16,
    animation: `in-scale 200ms var(--ease-sp) ${delay}ms both`,
    flexShrink: 0,
  }

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && (
        <span style={{
          background: 'var(--bg-2)', border: '1px solid var(--border-h)',
          borderRadius: 'var(--r-s)', padding: '4px 10px',
          fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font)',
          whiteSpace: 'nowrap', boxShadow: 'var(--sh-sm)',
          animation: 'in-fade 100ms',
        }}>
          {label}
        </span>
      )}
      <div style={circleStyle} onClick={onClick}>
        {icon}
      </div>
    </div>
  )
}

export function QuickCreate() {
  const [open, setOpen] = useState(false)
  const [activeItem, setActiveItem] = useState<QuickCreateItem | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [showSiteModal, setShowSiteModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { toast, toastError } = useToast()

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  function handleSelect(item: QuickCreateItem) {
    setOpen(false)
    // Chat opens the ChatWidget via global event
    if (item.id === 'chat') {
      window.dispatchEvent(new Event('open-chat-widget'))
      return
    }
    // Site uses its own modal
    if (item.id === 'site') {
      setShowSiteModal(true)
      return
    }
    setActiveItem(item)
    // Set defaults for selects
    const defaults: Record<string, string> = {}
    for (const f of item.fields) {
      if (f.type === 'select' && f.options?.length) defaults[f.name] = f.options[0].value
      else defaults[f.name] = ''
    }
    setFormData(defaults)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!activeItem) return
    setSaving(true)
    try {
      const result = await api.post<{ slug?: string }>(activeItem.endpoint, formData)
      toast(`${activeItem.label.replace('New ', '')} created`)
      setActiveItem(null)
      if (result?.slug && activeItem.navigateTo) {
        navigate(activeItem.navigateTo(result.slug))
      }
    } catch (err) {
      toastError('Failed to create', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {/* FAB Button */}
      <div ref={menuRef} style={{ position: 'fixed', bottom: 88, right: 24, zIndex: 90 }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            width: 50, height: 50, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent), var(--accent-h))',
            color: 'var(--accent-t)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 300, lineHeight: 1,
            boxShadow: open ? 'var(--sh-glow-s)' : 'var(--sh-glow)',
            transition: 'all var(--dur-n) var(--ease-sp)',
            transform: open ? 'rotate(45deg) scale(1.05)' : 'none',
          }}
          title="Quick create"
        >
          +
        </button>

        {/* Glassmorphic menu items */}
        {open && (
          <div style={{
            position: 'absolute', bottom: 62, right: 4,
            display: 'flex', flexDirection: 'column', gap: 8,
            alignItems: 'flex-end',
          }}>
            {QUICK_ITEMS.map((item, i) => (
              <FABMenuItem
                key={item.id}
                label={item.label}
                icon={item.icon}
                onClick={() => handleSelect(item)}
                delay={(QUICK_ITEMS.length - 1 - i) * 40}
              />
            ))}
          </div>
        )}
      </div>

      {/* Creation drawer (for non-site items) */}
      <Drawer isOpen={!!activeItem} onClose={() => setActiveItem(null)} title={activeItem?.label || 'Create'}>
        {activeItem && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {activeItem.fields.map(field => (
              <div key={field.name}>
                {field.type === 'select' ? (
                  <>
                    <label className="label" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
                      {field.label}{field.required && ' *'}
                    </label>
                    <select
                      className="select"
                      value={formData[field.name] || ''}
                      onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                      required={field.required}
                    >
                      {field.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </>
                ) : field.type === 'textarea' ? (
                  <>
                    <label className="label" style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>
                      {field.label}{field.required && ' *'}
                    </label>
                    <textarea
                      className="input"
                      rows={3}
                      value={formData[field.name] || ''}
                      onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                      required={field.required}
                      placeholder={field.placeholder}
                      style={{ resize: 'vertical' }}
                    />
                  </>
                ) : (
                  <Input
                    label={`${field.label}${field.required ? ' *' : ''}`}
                    type={field.type}
                    value={formData[field.name] || ''}
                    onChange={(v) => setFormData({ ...formData, [field.name]: v })}
                    placeholder={field.placeholder}
                  />
                )}
              </div>
            ))}
            <Button type="submit" loading={saving} style={{ marginTop: 8 }}>
              Create
            </Button>
          </form>
        )}
      </Drawer>

      {/* Site creation modal (reusable) */}
      <SiteCreateModal
        isOpen={showSiteModal}
        onClose={() => setShowSiteModal(false)}
        onCreated={(site) => {
          toast('Site created')
          setShowSiteModal(false)
          if (site.slug) navigate(`/sites/${site.slug}`)
        }}
      />
    </>
  )
}
